import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { RouteComponentProps } from 'react-router-dom';
import { compose } from 'ramda';
import queryString from 'query-string';
import {
  BackTo,
  Typography,
  Button,
  Select,
  Modal,
  showToast,
} from '@deeptrue-frontend/ui';
import { Dashboard } from 'components/Layout';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { ProjectType } from 'store/modules/projects/projects.types';
import { OrganizationType } from 'store/modules/auth/auth';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import { withProject } from '../withProject';
import { useFormatMessage } from 'components/IntlProvider';
import api from 'api';
import { TemplateGroup, TemplateType } from 'consts/template';
import ProjectSendInvitations from '../CollectData/ManualDataCollection/ProjectSendInvitations';
import { generatePreview } from './generatePreview';
import RemoveTemplate from './RemoveTemplate';
import { ProjectStatus } from 'consts/statuses';

const { connect, selectors } = StoreProvider;

interface TemplateTypeDropdown extends TemplateType {
  value: string;
  label: string;
}

const StyledModal = styled(Modal)`
  max-width: 58rem;
  max-height: 90%;
  min-height: 10rem;
  overflow-y: auto;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ButtonNoStyles = styled.button`
  background-color: transparent;
  outline: none;
  border: none;
  display: inherit;
`;

const DropdownWrapper = styled.div`
  background-color: white;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  border: 1px solid #e3e5e8;
  box-shadow: 0px 2px 4px rgba(63, 63, 68, 0.041958),
    0px 1px 1px rgba(63, 63, 68, 0.0769231);
  border-radius: 4px;
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const DropdownTexts = styled(Typography)<{ hoverColor?: string }>`
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    ${({ hoverColor }) =>
      hoverColor &&
      `
      color: ${hoverColor};
 
    `}
  }
`;

const Footer = styled.div`
  border-top: 1px solid #e3e5e8;
  padding-top: 1.5rem;
  margin-top: 3rem;
  margin-bottom: 3rem;
  display: flex;
  justify-content: flex-end;
`;

const TemplateSettings = styled.div`
  display: flex;
`;

interface Props extends RouteComponentProps<{ id: string; codename: string }> {
  project: ProjectType;
  organization: OrganizationType;
  updateProject: () => void;
}

const EmailTemplates = ({
  project,
  organization,
  location,
  history,
  match: { params },
  updateProject,
}: Props) => {
  const [isSendInitationModalOpen, setSendInvitationModalOpen] = useState(
    false
  );
  const [surveyUiid, setSurveyUiid] = useState<string | null>(null);
  const [respondentsList, setRespondentsList] = useState({
    count: 0,
    results: [],
    next: null,
  });
  const [templates, setTemplates] = useState<{
    emailTemplates: Array<TemplateTypeDropdown>;
    reminderTemplates: Array<TemplateTypeDropdown>;
  }>({
    emailTemplates: [],
    reminderTemplates: [],
  });
  const [selectedEmailTemplate, setEmailTemplate] = useState<
    TemplateTypeDropdown | undefined
  >(undefined);
  const [selectedReminderTemplate, setReminderTemplate] = useState<
    TemplateTypeDropdown | undefined
  >(undefined);
  const [removeTemplateModal, setRemoveTemplateModal] = useState<{
    isOpen: boolean;
    templateToRemove: TemplateTypeDropdown | null;
  }>({
    isOpen: false,
    templateToRemove: null,
  });
  const { created, updated } = queryString.parse(location.search);

  const isReminderTemplateVisible = project.send_reminders;

  const t = useFormatMessage();

  const invitationTextWithReminders = t(
    'templates.base.title',
    'Select an invitation and reminder templates or create the custom ones.'
  );
  const invitationTextWithoutReminders = t(
    'templates.base.title_no_reminder',
    'Select a template or create a custom one.'
  );

  useEffect(() => {
    if (created === 'success') {
      showToast({
        type: 'success',
        title: t('templates.base.created.success', 'Template created.'),
        id: 'template-created-success',
      });
    }
    if (updated === 'success') {
      showToast({
        type: 'success',
        title: t('templates.base.updated.success', 'Template updated.'),
        id: 'card-failure',
      });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    api.resources.templates
      .getEmailTemplates({
        orgName: params.codename || organization.codename,
      })
      .then(({ data }: any) => {
        const emailTemplates: Array<TemplateTypeDropdown> = [];
        const reminderTemplates: Array<TemplateTypeDropdown> = [];

        data.forEach((template: any) => {
          if (template.group === TemplateGroup.INVITATION) {
            emailTemplates.push({
              ...template,
              value: template.slug,
              label: template.name,
            });
          } else {
            reminderTemplates.push({
              ...template,
              value: template.slug,
              label: template.name,
            });
          }
        });

        setTemplates({
          emailTemplates: emailTemplates,
          reminderTemplates: reminderTemplates,
        });
      });

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (project.id !== -1) {
      api.resources
        .getProjectSurveys({
          orgName: params.codename || organization.codename,
          uuid: project.uuid,
        })
        .then(({ data }) => {
          if (data.results.length) {
            const { uuid } = data.results[0];

            setSurveyUiid(uuid);
          }
        })
        .catch(() => {
          showToast({
            type: 'error',
            title: t(
              'errors.toast.general',
              'Something went wrong, please contact administrator'
            ),
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  useEffect(() => {
    if (surveyUiid) {
      api.resources
        .getRespondentsList({
          uuid: surveyUiid,
          parsedSearch: { ordering: '-Created at', limit: 1 },
        })
        .then(({ data }: any) => {
          if (data) {
            setRespondentsList({
              ...data,
              results: [...respondentsList.results, ...data.results],
            });
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyUiid]);

  const isContinueButtonEnabled = project.send_reminders
    ? selectedEmailTemplate && selectedReminderTemplate
    : selectedEmailTemplate;

  const deleteTemplate = (template: TemplateTypeDropdown | undefined) => {
    if (!template) return;

    const { group, id } = template;

    api.resources.templates
      .deleteTemplate({
        orgName: params.codename || organization.codename,
        templateId: id,
      })
      .then(() => {
        const { emailTemplates, reminderTemplates } = templates;
        if (group === 'invitation') {
          const indexToRemove = emailTemplates.findIndex(
            (template: TemplateTypeDropdown) => template.id === id
          );
          emailTemplates.splice(indexToRemove, 1);
        } else {
          const indexToRemove = reminderTemplates.findIndex(
            (template: TemplateTypeDropdown) => template.id === id
          );
          reminderTemplates.splice(indexToRemove, 1);
        }
        if (group === 'invitation') {
          setEmailTemplate(undefined);
        } else {
          setReminderTemplate(undefined);
        }

        setTemplates({
          emailTemplates: emailTemplates,
          reminderTemplates: reminderTemplates,
        });

        setRemoveTemplateModal({
          isOpen: false,
          templateToRemove: null,
        });
      })
      .catch(() => {
        setRemoveTemplateModal({
          isOpen: false,
          templateToRemove: null,
        });
      });
  };

  const isEmailTemplateCustom =
    selectedEmailTemplate && selectedEmailTemplate.type === 'custom';
  const isReminderTemplateCustom =
    selectedReminderTemplate && selectedReminderTemplate.type === 'custom';

  const { final_fee } = project;

  const emailInputUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/collect-data/email`
    : `/project/${project.id}/collect-data/email`;
  const customTemplateUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/collect-data/template-email/create`
    : `/project/${project.id}/collect-data/template-email/create`;
  const editTemplateUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/collect-data/template-email/edit`
    : `/project/${project.id}/collect-data/template-email/edit`;

  const hasDataCollectionStarted =
    project.status.value === ProjectStatus.COLLECTING_DATA;

  return (
    <Dashboard
      title={project.name || t('page.project', 'Project details')}
      location={location}
      combinedTitle={project.combinedTitle}
    >
      <BackTo onClick={() => history.push(emailInputUrl)}>
        {t(
          'project.collectData.backTo_emails_input',
          'Back to Input of Email Addresses'
        )}
      </BackTo>

      <Wrapper style={{ maxWidth: '39rem' }}>
        <Typography variant="h5" fontSize="1.25rem">
          Email Templates
        </Typography>
        <Typography
          variant="copy1"
          fontSize="0.875rem"
          color="#5C6370"
          weight="regular"
        >
          {isReminderTemplateVisible
            ? invitationTextWithReminders
            : invitationTextWithoutReminders}
        </Typography>
        <section>
          <Typography variant="copy1" weight="medium" color="253858" mt="2rem">
            {t('templates.email.title', 'Invitation Template')}
          </Typography>
          <Typography
            variant="copy1"
            fontSize="0.875rem"
            color="#5C6370"
            weight="regular"
            mb="1rem"
          >
            {t(
              'templates.email.desc',
              'Choose a template for the invitation or create custom one.'
            )}
          </Typography>

          <DropdownWrapper>
            <DropdownHeader>
              <Typography weight="semiBold" fontSize="0.875rem">
                {t('templates.base.template', 'Template')}
              </Typography>
              <TemplateSettings>
                {isEmailTemplateCustom && (
                  <ButtonNoStyles
                    onClick={() =>
                      setRemoveTemplateModal({
                        isOpen: true,
                        templateToRemove: selectedEmailTemplate || null,
                      })
                    }
                  >
                    <DropdownTexts color="#DC3545" hoverColor="#821721">
                      {t('templates.base.delete', 'Delete')}
                    </DropdownTexts>
                  </ButtonNoStyles>
                )}
                {isEmailTemplateCustom && (
                  <ButtonNoStyles
                    onClick={() =>
                      history.push(
                        `${editTemplateUrl}/${selectedEmailTemplate?.id}`
                      )
                    }
                  >
                    <DropdownTexts ml="1rem">
                      {t('templates.base.edit', 'Edit')}
                    </DropdownTexts>
                  </ButtonNoStyles>
                )}
                {selectedEmailTemplate && (
                  <ButtonNoStyles
                    onClick={() => {
                      generatePreview(selectedEmailTemplate.content, true);
                    }}
                  >
                    <DropdownTexts
                      ml="1rem"
                      color="#1656E9"
                      hoverColor="#043094"
                    >
                      {t('templates.base.preview', 'Preview')}
                    </DropdownTexts>
                  </ButtonNoStyles>
                )}
              </TemplateSettings>
            </DropdownHeader>
            <Select
              name="emails"
              placeholder={t(
                'templates.base.email.placeholder',
                'Please Select Email template'
              )}
              onChange={(_fieldName: string, value: string) => {
                const templateToSave = templates.emailTemplates.find(
                  (template: TemplateTypeDropdown) => template.slug === value
                );
                setEmailTemplate(templateToSave);
              }}
              options={templates.emailTemplates}
              value={selectedEmailTemplate && selectedEmailTemplate.slug}
            />
            <ButtonNoStyles
              style={{ cursor: 'pointer' }}
              onClick={() => history.push(customTemplateUrl)}
            >
              <DropdownTexts color="#1656E9" hoverColor="#043094">
                + {t('templates.base.create', 'Create a Custom Template')}
              </DropdownTexts>
            </ButtonNoStyles>
          </DropdownWrapper>
        </section>

        {isReminderTemplateVisible && (
          <section>
            <Typography
              variant="copy1"
              weight="medium"
              color="253858"
              mt="2rem"
            >
              {t('templates.base.reminder', 'Reminder Template')}
            </Typography>
            <Typography
              variant="copy1"
              fontSize="0.875rem"
              color="#5C6370"
              weight="regular"
              mb="1rem"
            >
              {t(
                'templates.reminder.desc',
                'Choose a template for the reminder or create custom one.'
              )}
            </Typography>

            <DropdownWrapper>
              <DropdownHeader>
                <Typography weight="semiBold" fontSize="0.875rem">
                  {t('templates.base.template', 'Template')}
                </Typography>
                <TemplateSettings>
                  {isReminderTemplateCustom && (
                    <ButtonNoStyles
                      onClick={() =>
                        setRemoveTemplateModal({
                          isOpen: true,
                          templateToRemove: selectedReminderTemplate || null,
                        })
                      }
                    >
                      <DropdownTexts color="#DC3545" hoverColor="#821721">
                        {t('templates.base.delete', 'Delete')}
                      </DropdownTexts>
                    </ButtonNoStyles>
                  )}
                  {isReminderTemplateCustom && (
                    <ButtonNoStyles
                      onClick={() =>
                        history.push(
                          `${editTemplateUrl}/${selectedEmailTemplate?.id}`
                        )
                      }
                    >
                      <DropdownTexts ml="1rem">
                        {t('templates.base.edit', 'Edit')}
                      </DropdownTexts>
                    </ButtonNoStyles>
                  )}
                  {selectedReminderTemplate && (
                    <ButtonNoStyles
                      onClick={() => {
                        generatePreview(selectedReminderTemplate.content, true);
                      }}
                    >
                      <DropdownTexts
                        ml="1rem"
                        color="#1656E9"
                        hoverColor="#043094"
                      >
                        {t('templates.base.preview', 'Preview')}
                      </DropdownTexts>
                    </ButtonNoStyles>
                  )}
                </TemplateSettings>
              </DropdownHeader>
              <Select
                name="reminders"
                placeholder={t(
                  'templates.base.reminder.placeholder',
                  'Please Select Reminder template'
                )}
                onChange={(_fieldName: string, value: string) => {
                  const templateToSave = templates.reminderTemplates.find(
                    (template: TemplateTypeDropdown) => template.slug === value
                  );

                  setReminderTemplate(templateToSave);
                }}
                options={templates.reminderTemplates}
                value={
                  selectedReminderTemplate && selectedReminderTemplate.slug
                }
              />
              <ButtonNoStyles
                style={{ cursor: 'pointer' }}
                onClick={() => history.push(customTemplateUrl)}
              >
                <DropdownTexts color="#1656E9" hoverColor="#043094">
                  + {t('templates.base.create', 'Create a Custom Template')}
                </DropdownTexts>
              </ButtonNoStyles>
            </DropdownWrapper>
          </section>
        )}

        <Footer>
          <Button
            onClick={() =>
              history.push(`/project/${project.id}/collect-data/email`)
            }
            variant="outlined"
            mr="0.5rem"
          >
            {t('templates.base.back', 'Back')}
          </Button>
          <Button
            data-testid="templates-continue-button"
            disabled={!isContinueButtonEnabled}
            onClick={() => setSendInvitationModalOpen(true)}
          >
            {t('templates.base.continue', 'Continue')}
          </Button>
        </Footer>
      </Wrapper>

      <StyledModal
        isVisible={isSendInitationModalOpen}
        onBackdropClick={() => setSendInvitationModalOpen(false)}
        onCloseClick={() => setSendInvitationModalOpen(false)}
      >
        <ProjectSendInvitations
          location={location}
          projectId={project.id}
          params={params}
          surveyUiid={surveyUiid}
          history={history}
          closeModal={() => setSendInvitationModalOpen(false)}
          updateProject={updateProject}
          credits={final_fee}
          voucherInfo={project.voucher}
          templateEmail={selectedEmailTemplate}
          templateReminder={selectedReminderTemplate}
          hasDataCollectionStarted={hasDataCollectionStarted}
        />
      </StyledModal>

      <Modal
        isVisible={removeTemplateModal.isOpen}
        onBackdropClick={() =>
          setRemoveTemplateModal({
            isOpen: false,
            templateToRemove: null,
          })
        }
        onCloseClick={() =>
          setRemoveTemplateModal({
            isOpen: false,
            templateToRemove: null,
          })
        }
      >
        <RemoveTemplate
          onCancel={() =>
            setRemoveTemplateModal({
              isOpen: false,
              templateToRemove: null,
            })
          }
          templateName={
            removeTemplateModal.templateToRemove
              ? removeTemplateModal.templateToRemove.name
              : null
          }
          onConfirm={() =>
            deleteTemplate(removeTemplateModal.templateToRemove || undefined)
          }
        />
      </Modal>
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
  })),
  withMetaDescription({ title: 'Dashboard projects' }),
  withProject(({ match, organization }: Props) => ({
    projectId: match.params.id,
    organizationCodename: match.params.codename || organization.codename,
  }))
)(EmailTemplates);
